import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { ADMIN_BASE_URL } from "../config/config";
import "../components/App.css";

const emptyExecutive = {
  slug: "",
  name: "",
  role: "",
  bio: "",
  imageUrl: "",
  linkedinUrl: "",
  twitterUrl: "",
  githubUrl: "",
  sortOrder: 0,
  isActive: true,
};

const AdminTeam = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [customRecords, setCustomRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyExecutive);
  const [photoFile, setPhotoFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("care_admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [mergedRes, defaultsRes, customRes] = await Promise.all([
        axios.get(`${ADMIN_BASE_URL}/team/merged`, { headers: getAuthHeaders() }),
        axios.get(`${ADMIN_BASE_URL}/team/defaults`, { headers: getAuthHeaders() }),
        axios.get(`${ADMIN_BASE_URL}/team`, { headers: getAuthHeaders() }),
      ]);
      setTeamMembers(mergedRes.data || []);
      setDefaults(defaultsRes.data || []);
      setCustomRecords(customRes.data || []);
    } catch (error) {
      toast.error("Failed to load leadership team data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openCustomizeDefault = (member) => {
    const existing = customRecords.find((record) => record.slug === member.slug);
    setEditingId(existing?._id || null);
    setForm({
      slug: member.slug,
      name: member.name,
      role: member.role,
      bio: member.bio,
      imageUrl: member.imageUrl || "",
      linkedinUrl: member.linkedinUrl || "",
      twitterUrl: member.twitterUrl || "",
      githubUrl: member.githubUrl || "",
      sortOrder: member.sortOrder || 0,
      isActive: true,
    });
    setPhotoFile(null);
    setShowModal(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyExecutive);
    setPhotoFile(null);
    setShowModal(true);
  };

  const openEditCustom = (member) => {
    const record = customRecords.find((item) => item.slug === member.slug);
    if (!record) {
      openCustomizeDefault(member);
      return;
    }
    setEditingId(record._id);
    setForm({
      slug: record.slug,
      name: record.name,
      role: record.role,
      bio: record.bio,
      imageUrl: record.imageUrl || "",
      linkedinUrl: record.linkedinUrl || "",
      twitterUrl: record.twitterUrl || "",
      githubUrl: record.githubUrl || "",
      sortOrder: record.sortOrder || 0,
      isActive: record.isActive !== false,
    });
    setPhotoFile(null);
    setShowModal(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      payload.append(key, value ?? "");
    });
    if (photoFile) {
      payload.append("photo", photoFile);
    }

    try {
      if (editingId) {
        await axios.put(`${ADMIN_BASE_URL}/team/${editingId}`, payload, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        });
        toast.success("Executive updated");
      } else {
        await axios.post(`${ADMIN_BASE_URL}/team`, payload, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        });
        toast.success("Executive saved");
      }
      setShowModal(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save executive");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (member) => {
    const record = customRecords.find((item) => item.slug === member.slug);
    if (!record) {
      toast.error("This profile is still using the default content.");
      return;
    }
    if (!window.confirm(`Remove custom profile for ${member.name}? The default profile will be restored.`)) {
      return;
    }
    try {
      await axios.delete(`${ADMIN_BASE_URL}/team/${record._id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Custom profile removed");
      fetchAll();
    } catch (error) {
      toast.error("Failed to remove custom profile");
    }
  };

  return (
    <div className="admin-faq-content">
      <div className="admin-faq-header">
        <div>
          <h2>Leadership & Core Team</h2>
          <p className="text-muted mb-0">
            Customize default executives or add new team members for the public leadership page.
          </p>
        </div>
        <button type="button" className="admin-faq-btn" onClick={openCreate}>
          Add Executive
        </button>
      </div>

      {loading ? (
        <p>Loading team members...</p>
      ) : (
        <div className="admin-faq-table-wrap">
          <table className="admin-faq-table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teamMembers.length === 0 ? (
                <tr>
                  <td colSpan="5">No team members found.</td>
                </tr>
              ) : (
                teamMembers.map((member) => (
                  <tr key={member.slug}>
                    <td>
                      {member.imageUrl ? (
                        <img
                          src={member.imageUrl}
                          alt={member.name}
                          style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8 }}
                        />
                      ) : (
                        member.initials
                      )}
                    </td>
                    <td>{member.name}</td>
                    <td>{member.role}</td>
                    <td>{member.isCustomized ? "Custom" : "Default"}</td>
                    <td>
                      <div className="admin-faq-actions">
                        <button
                          type="button"
                          className="admin-faq-btn"
                          onClick={() =>
                            member.isCustomized ? openEditCustom(member) : openCustomizeDefault(member)
                          }
                        >
                          {member.isCustomized ? "Edit" : "Customize"}
                        </button>
                        {member.isCustomized && (
                          <button
                            type="button"
                            className="admin-faq-btn admin-faq-btn--danger"
                            onClick={() => handleDelete(member)}
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Edit Executive" : "Add / Customize Executive"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Slug</Form.Label>
              <Form.Control
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="e.g. hiroshi-tanaka"
                required
                disabled={Boolean(editingId)}
              />
              {!editingId && defaults.length > 0 && (
                <Form.Text className="text-muted">
                  Use an existing default slug to replace a default profile, or enter a new slug to add a member.
                </Form.Text>
              )}
            </Form.Group>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Control
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    required
                  />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Bio</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Photo</Form.Label>
              <Form.Control
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
              />
              {form.imageUrl && !photoFile && (
                <div className="mt-2">
                  <img
                    src={form.imageUrl}
                    alt={form.name}
                    style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8 }}
                  />
                </div>
              )}
            </Form.Group>
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>LinkedIn URL</Form.Label>
                  <Form.Control
                    value={form.linkedinUrl}
                    onChange={(e) => setForm({ ...form, linkedinUrl: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Twitter URL</Form.Label>
                  <Form.Control
                    value={form.twitterUrl}
                    onChange={(e) => setForm({ ...form, twitterUrl: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>GitHub URL</Form.Label>
                  <Form.Control
                    value={form.githubUrl}
                    onChange={(e) => setForm({ ...form, githubUrl: e.target.value })}
                  />
                </Form.Group>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Sort Order</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="team-is-active"
                    label="Active on public page"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                </Form.Group>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="success" type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminTeam;
